/* eslint-disable @typescript-eslint/no-explicit-any */
 
import { z } from 'zod';
import { GAME_CONFIG } from '../constants/gameConfig';
import packageJson from '../../package.json' with { type: 'json' };

// Basic date schema
const gameDate = z.object({
  day: z.number().min(1).max(30),
  month: z.number().min(1).max(12),
  year: z.number().min(GAME_CONFIG.STARTING_YEAR),
  hour: z.number().min(0).max(23).optional(),
  minute: z.number().min(0).max(59).optional()
});

// Company metrics schema
const companyMetrics = z.object({
  cash: z.number(),
  reputation: z.number().min(0).max(100),
  marketShare: z.number().min(0).max(1),
  researchPoints: z.number().min(0),
  bankruptcyWarnings: z.number().min(0).max(3),
  isGameOver: z.boolean()
});

// Financial stats schema - separate from basic metrics
const financialSchema = z.object({
  cash: z.number(),
  revenue: z.number(),
  costs: z.number(),
  profit: z.number(),
  dailyStats: z.object({
    revenue: z.number(),
    costs: z.number(),
    profit: z.number(),
    salesBreakdowns: z.array(z.object({
      productId: z.string(),
      revenue: z.number(),
      unitsSold: z.number()
    })).optional()
  }),
  monthlyStats: z.object({
    revenue: z.number(),
    costs: z.number(),
    profit: z.number()
  }),
  yearlyStats: z.object({
    revenue: z.number(),
    costs: z.number(),
    profit: z.number()
  })
});

// Research tree progress schema
const researchProgress = z.object({
  nodeId: z.string(),
  startDate: gameDate,
  progress: z.number().min(0).max(100),
  completed: z.boolean()
});

// Import the product schema
import { productSchema } from './product';

// Employee schema
const employeeSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  salary: z.number(),
  skills: z.record(z.string(), z.number()),
  experience: z.number(),
  performance: z.number(),
  happiness: z.number()
});

// Hiring process schema
const hiringProcessSchema = z.object({
  role: z.string(),
  count: z.number(),
  startDate: gameDate,
  daysRemaining: z.number()
});

// Market state schema
const marketStateSchema = z.object({
  totalMarketSize: z.number(),
  marketGrowthRate: z.number(),
  economicPhase: z.string(),
  competitorActions: z.array(z.any()).default([]),
  technologicalDisruptions: z.array(z.any()).default([]),
  marketEvents: z.array(z.any()).default([]),
  playerActions: z.array(z.any()).default([])
});

// Research state schema for the save file
const researchStateSchema = z.object({
  unlockedNodes: z.array(z.string()).default([]),
  activeResearch: z.array(researchProgress).default([]),
  researchPoints: z.number().min(0).default(0),
  monthlyInvestment: z.number().min(0).default(0),
  rpGenerationRate: z.number().min(1).default(1)
});

// Event state schema
const eventStateSchema = z.object({
  eventHistory: z.array(z.any()).default([]),
  pendingReactiveEvents: z.array(z.any()).default([]),
  systemicNotifications: z.array(z.any()).default([]),
  marketConditions: z.record(z.string(), z.number()).default({})
});

// Production batch schema
const productionBatchSchema = z.object({
  id: z.string(),
  productId: z.string(),
  startDate: z.date(),
  targetUnits: z.number(),
  completedUnits: z.number(),
  qualityScore: z.number(),
  defectRate: z.number(),
  status: z.enum(['in-progress', 'completed']),
  costs: z.object({
    materials: z.number(),
    labor: z.number(),
    overhead: z.number()
  })
});

// Production line schema
const productionLineSchema = z.object({
  id: z.string(),
  factoryBuildingId: z.string(),
  lineNumber: z.number(),
  productId: z.string().nullable(),
  productName: z.string().optional(),
  lastProductChange: gameDate.optional(),
  allocatedEmployees: z.number(),
  dailyOutput: z.number(),
  efficiency: z.number(),
  qualityScore: z.number(),
  status: z.enum(['idle', 'active', 'paused', 'maintenance']),
  pauseReason: z.enum(['no_product', 'no_employees', 'no_components', 'maintenance']).optional()
});

// Building schema
const buildingSchema = z.object({
  id: z.string(),
  buildingTypeId: z.string(),
  purchaseDate: gameDate,
  constructionCompleteDate: gameDate.optional(),
  isUnderConstruction: z.boolean().default(false),
  name: z.string().optional()
});

// Employee allocation schema - tracks which employees are allocated to products
const employeeAllocationSchema = z.object({
  productId: z.string(),
  phase: z.enum(['design', 'engineering', 'testing', 'manufacturing', 'production']),
  employeeType: z.string(),
  count: z.number(),
  employeeIds: z.array(z.string()).optional()
});

// Daily stats schema
const dailyStatsSchema = z.object({
  revenue: z.number(),
  costs: z.number(),
  profit: z.number(),
  salesBreakdowns: z.array(z.object({
    productId: z.string(),
    unitsSold: z.number(),
    revenue: z.number()
  })).default([])
});

// Component order schema
const componentOrderSchema = z.object({
  orderId: z.string(),
  componentId: z.string(),
  componentName: z.string(),
  quantity: z.number(),
  unitCost: z.number(),
  totalCost: z.number(),
  orderDate: z.date(),
  expectedDeliveryDate: z.date(),
  deliveryDate: z.date().optional(),
  status: z.enum(['pending', 'in_transit', 'delivered', 'cancelled']),
  gameOrderDate: gameDate.optional(),
  gameDeliveryDate: gameDate.optional()
});

// Auto order setting schema
const autoOrderSettingSchema = z.object({
  componentId: z.string(),
  quantity: z.number(),
  orderOnZeroInventory: z.boolean().optional(), // Deprecated
  autoOrderThreshold: z.number().nullable(),
  recurringOrder: z.object({
    enabled: z.boolean(),
    frequency: z.enum(['month', 'quarter', 'year']),
    lastOrderDate: gameDate.optional()
  })
});

// Component inventory schema
const componentInventorySchema = z.object({
  id: z.string(),
  componentId: z.string(),
  componentName: z.string(),
  category: z.string(),
  currentStock: z.number(),
  minStock: z.number(),
  maxStock: z.number(),
  unitCost: z.number(),
  consumptionRate: z.number(),
  lastOrderDate: z.date().optional(),
  lastOrderQuantity: z.number().optional(),
  leadTimeDays: z.number()
});

// Device inventory schema
const deviceInventorySchema = z.object({
  id: z.string(),
  productId: z.string(),
  productName: z.string(),
  currentStock: z.number(),
  productionRate: z.number(),
  targetStock: z.number(),
  qualityScore: z.number()
});

// Inventory state schema
const inventoryStateSchema = z.object({
  components: z.array(componentInventorySchema).default([]),
  devices: z.array(deviceInventorySchema).default([]),
  pendingOrders: z.array(componentOrderSchema).default([]),
  autoOrderSettings: z.record(z.string(), autoOrderSettingSchema).default({})
});

// Spatial navigation state schema
const spatialStateSchema = z.object({
  currentLocation: z.string().default('campus'),
  unlockedLocations: z.array(z.string()).default(['campus', 'hq', 'design-studio']),
  transitionPreferences: z.object({
    enabled: z.boolean().default(true),
    reducedMotion: z.boolean().default(false),
    defaultType: z.enum(['fade', 'slide', 'zoom', 'none']).default('fade'),
    defaultDuration: z.number().default(300)
  }).optional()
}).optional();

// Supply/Demand history data point schema
const supplyDemandPointSchema = z.object({
  date: gameDate,
  supply: z.number(),      // Available inventory at that time
  demand: z.number(),      // Calculated market demand at that time
  unitsSold: z.number()    // Actual sales (constrained by min of supply/demand)
});

// Supply/Demand history per product schema
const productSupplyDemandHistorySchema = z.object({
  productId: z.string(),
  history: z.array(supplyDemandPointSchema).default([])
});

// Supply/Demand history state schema
const supplyDemandHistoryStateSchema = z.object({
  productHistories: z.array(productSupplyDemandHistorySchema).default([])
}).optional();

// Financial history schemas
const monthlyFinancialSnapshotSchema = z.object({
  date: z.object({
    month: z.number().min(1).max(12),
    year: z.number().min(GAME_CONFIG.STARTING_YEAR)
  }),
  revenue: z.number(),
  costs: z.number(),
  profit: z.number(),
  cash: z.number()
});

const financialHistoryStateSchema = z.object({
  monthlySnapshots: z.array(monthlyFinancialSnapshotSchema).default([])
}).default({ monthlySnapshots: [] });

const currentMonthAccumulatorSchema = z.object({
  revenue: z.number(),
  costs: z.number()
}).default({ revenue: 0, costs: 0 });

// Game save schema
export const gameSaveSchema = z.object({
  version: z.string(),
  saveDate: z.date(),
  gameDate: gameDate,
  company: z.object({
    id: z.string(),
    name: z.string(),
    ceoName: z.string(),
    founded: gameDate,
    metrics: companyMetrics,
    finances: financialSchema.optional(),
    research: researchStateSchema.optional(),
    products: z.array(productSchema),
    marketingCampaigns: z.array(z.any()),
    factories: z.array(z.any()),
    employees: z.array(employeeSchema).default([]),
    hiringProcesses: z.array(hiringProcessSchema).default([]),
    buildings: z.array(buildingSchema).default([]),
    employeeAllocations: z.array(employeeAllocationSchema).default([])
  }),
  marketState: marketStateSchema.optional(),
  eventState: eventStateSchema.optional(),
  productionBatches: z.array(productionBatchSchema).default([]),
  productionLines: z.array(productionLineSchema).default([]),
  competitorProducts: z.array(productSchema).default([]), // Added field for competitor products
  dailyStats: dailyStatsSchema.optional(),
  inventoryState: inventoryStateSchema.optional(),
  spatialState: spatialStateSchema,
  supplyDemandHistory: supplyDemandHistoryStateSchema,
  financialHistory: financialHistoryStateSchema,
  currentMonthAccumulator: currentMonthAccumulatorSchema,
  settings: z.object({
    gameSpeed: z.number().min(0).max(8),
    autosaveEnabled: z.boolean(),
    autosaveInterval: z.number().min(1),
    fireWarningEnabled: z.boolean().default(true),
    navigationMode: z.enum(['spatial', 'classic']).default('spatial'),
    phaseProgressionPreferences: z.object({
      design_complete: z.enum(['proceed', 'minor', 'major', 'ask']).default('ask'),
      engineering_complete: z.enum(['proceed', 'minor', 'major', 'ask']).default('ask'),
      testing_complete: z.enum(['proceed', 'minor', 'major', 'ask']).default('ask'),
      manufacturing_complete: z.enum(['proceed', 'minor', 'major', 'ask']).default('ask')
    }).default({
      design_complete: 'ask',
      engineering_complete: 'ask',
      testing_complete: 'ask',
      manufacturing_complete: 'ask'
    }),
    phasePreferencesLastChanged: z.date().optional(),
    audio: z.object({
      musicEnabled: z.boolean().default(true),
      musicVolume: z.number().min(0).max(1).default(0.5),
      currentTrack: z.number().min(0).default(0)
    }).default({
      musicEnabled: true,
      musicVolume: 0.5,
      currentTrack: 0
    })
  }),
  statistics: z.object({
    totalRevenue: z.number().min(0),
    totalProfit: z.number(),
    productsLaunched: z.number().min(0),
    marketSharePeak: z.number().min(0).max(1),
    bankruptcies: z.number().min(0),
    playTime: z.number().min(0)
  })
});

export type GameSave = z.infer<typeof gameSaveSchema>;
export type PhaseProgressionPreferences = GameSave['settings']['phaseProgressionPreferences'];
export type SupplyDemandPoint = z.infer<typeof supplyDemandPointSchema>;
export type ProductSupplyDemandHistory = z.infer<typeof productSupplyDemandHistorySchema>;
export type MonthlyFinancialSnapshot = z.infer<typeof monthlyFinancialSnapshotSchema>;
export type FinancialHistoryState = z.infer<typeof financialHistoryStateSchema>;
export type CurrentMonthAccumulator = z.infer<typeof currentMonthAccumulatorSchema>;
export type SavedProductionLine = z.infer<typeof productionLineSchema>;

export class GameSaveManager {
  private static readonly SAVE_VERSION = packageJson.version;
  private static readonly SAVE_PREFIX = 'phoneverse_save_';

  static createSave(gameState: {
    gameDate: { day: number; month: number; year: number };
    company: {
      id: string;
      name: string;
      ceoName: string;
      founded: { day: number; month: number; year: number };
      metrics: {
        cash: number;
        reputation: number;
        marketShare: number;
        researchPoints: number;
        bankruptcyWarnings: number;
        isGameOver: boolean;
      };
      finances?: unknown;
      research?: unknown;
      products: unknown[];
      marketingCampaigns: unknown[];
      factories: unknown[];
      employees?: unknown[];
      hiringProcesses?: unknown[];
      buildings?: unknown[];
    };
    marketState?: unknown;
    eventState?: unknown;
    productionBatches?: unknown[];
    productionLines?: unknown[];
    competitorProducts?: unknown[];
    dailyStats?: unknown;
    inventoryState?: unknown;
    spatialState?: {
      currentLocation: string;
      unlockedLocations: string[];
      transitionPreferences?: {
        enabled: boolean;
        reducedMotion: boolean;
        defaultType: 'fade' | 'slide' | 'zoom' | 'none';
        defaultDuration: number;
      };
    };
    supplyDemandHistory?: {
      productHistories: Array<{
        productId: string;
        history: Array<{
          date: { day: number; month: number; year: number };
          supply: number;
          demand: number;
          unitsSold: number;
        }>;
      }>;
    };
    settings: unknown;
    statistics: unknown;
  }): GameSave {
    // Transform products to match the required schema
    const transformedProducts = (gameState.company.products || []).map((product: Record<string, unknown>) => {
      // Extract nested objects safely
      const designConfig = product.designConfig as Record<string, unknown> || {};
      const metadata = product.metadata as Record<string, unknown> || {};
      const salesMetrics = product.salesMetrics as Record<string, unknown> || {};
      const developmentPhase = product.developmentPhase as Record<string, unknown> | undefined;
      const reviews = product.reviews as { professional?: unknown[]; customer?: unknown[] } | undefined;
      
      return {
        id: product.id,
        name: product.name,
        description: product.description || `${product.name} - Next Generation Device`,
        status: product.status,
        price: product.price || 0,
        developmentCost: product.developmentCost || 0,
        productionCost: product.productionCost || 0,
        techScore: product.techScore || 0,
        marketAppeal: product.marketAppeal || 0,
        designConfig: {
          name: designConfig.name || product.name,
          price: designConfig.price || product.price || 0,
          color: designConfig.color || '#000000',
          material: designConfig.material || 'plastic',
          components: designConfig.components || {},
          design: designConfig.design || {
            buttonColor: '#000000',
            hasButtons: true,
            screenBorderColor: '#000000',
            cameraStyle: 'default',
            cameraColor: '#000000',
            cameraPosition: { x: 0, y: 0 },
            buttonPositions: { volume: 0, power: 0 },
            phoneSize: { width: 0, height: 0, depth: 0 },
            screenSize: { width: 0, height: 0 },
            cornerRadius: 0
          },
          techScore: designConfig.techScore || product.techScore || 0,
          marketAppeal: designConfig.marketAppeal || product.marketAppeal || 0
        },
        metadata: {
          companyId: gameState.company.id,
          designDate: metadata.designDate || {
            day: gameState.gameDate.day,
            month: gameState.gameDate.month,
            year: gameState.gameDate.year
          },
          ...(metadata.releaseDate ? {
            releaseDate: metadata.releaseDate
          } : {}),
          ...(metadata.discontinuedDate ? {
            discontinuedDate: metadata.discontinuedDate
          } : {}),
          // CRITICAL FIX: Save the isReleased flag
          ...(metadata.isReleased !== undefined ? {
            isReleased: metadata.isReleased
          } : {})
        },
        salesMetrics: {
          // CRITICAL FIX: Access salesMetrics from the nested object
          unitsSold: salesMetrics.unitsSold || 0,
          revenue: salesMetrics.revenue || 0,
          averageRating: salesMetrics.averageRating || 0,
          marketShare: salesMetrics.marketShare || 0
        },
        // Include reviews if they exist
        ...(reviews ? {
          reviews: {
            professional: reviews.professional || [],
            customer: reviews.customer || [],
          }
        } : {}),
        // CRITICAL FIX: Include developmentPhase if it exists
        ...(developmentPhase ? {
          developmentPhase: {
            phase: developmentPhase.phase,
            startDate: developmentPhase.startDate,
            estimatedDuration: developmentPhase.estimatedDuration || 30,
            actualDuration: developmentPhase.actualDuration,
            budget: developmentPhase.budget || 0,
            confidence: developmentPhase.confidence || 50,
            qualityScore: developmentPhase.qualityScore || 50,
            riskEvents: developmentPhase.riskEvents || [],
            investmentCount: developmentPhase.investmentCount || 0
          }
        } : {})
      };
    });

    // Helper function to sanitize numeric values
    const sanitizeNumber = (value: any): number => {
      if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
        return 0;
      }
      return value;
    };

    // Sanitize financial data if present
    let sanitizedFinances = undefined;
    if (gameState.company.finances) {
      const finances = gameState.company.finances as any;
      sanitizedFinances = {
        cash: sanitizeNumber(finances.cash),
        revenue: sanitizeNumber(finances.revenue),
        costs: sanitizeNumber(finances.costs),
        profit: sanitizeNumber(finances.profit),
        dailyStats: {
          revenue: sanitizeNumber(finances.dailyStats?.revenue),
          costs: sanitizeNumber(finances.dailyStats?.costs),
          profit: sanitizeNumber(finances.dailyStats?.profit),
          salesBreakdowns: finances.dailyStats?.salesBreakdowns || []
        },
        monthlyStats: {
          revenue: sanitizeNumber(finances.monthlyStats?.revenue),
          costs: sanitizeNumber(finances.monthlyStats?.costs),
          profit: sanitizeNumber(finances.monthlyStats?.profit)
        },
        yearlyStats: {
          revenue: sanitizeNumber(finances.yearlyStats?.revenue),
          costs: sanitizeNumber(finances.yearlyStats?.costs),
          profit: sanitizeNumber(finances.yearlyStats?.profit)
        }
      };
    }

    const save = {
      version: this.SAVE_VERSION,
      saveDate: new Date(),
      gameDate: gameState.gameDate,
      company: {
        id: gameState.company.id,
        name: gameState.company.name,
        ceoName: gameState.company.ceoName,
        founded: gameState.company.founded,
        metrics: {
          cash: isNaN(gameState.company.metrics.cash) ? 0 : gameState.company.metrics.cash,
          reputation: isNaN(gameState.company.metrics.reputation) ? 50 : gameState.company.metrics.reputation,
          marketShare: isNaN(gameState.company.metrics.marketShare) ? 0 : gameState.company.metrics.marketShare,
          researchPoints: isNaN(gameState.company.metrics.researchPoints) ? 0 : gameState.company.metrics.researchPoints,
          bankruptcyWarnings: gameState.company.metrics.bankruptcyWarnings || 0,
          isGameOver: gameState.company.metrics.isGameOver || false
        },
        finances: sanitizedFinances,
        research: gameState.company.research,
        products: transformedProducts,
        marketingCampaigns: gameState.company.marketingCampaigns,
        factories: gameState.company.factories,
        employees: (gameState.company.employees || []).map((emp: any) => ({
          ...emp,
          performance: isNaN(emp.performance) || emp.performance === null ? 50 : emp.performance,
          happiness: isNaN(emp.happiness) || emp.happiness === null ? 50 : emp.happiness,
          salary: isNaN(emp.salary) || emp.salary === null ? 50000 : emp.salary,
          productivity: isNaN(emp.productivity) || emp.productivity === null ? 1 : emp.productivity
        })),
        hiringProcesses: gameState.company.hiringProcesses || [],
        buildings: gameState.company.buildings || []
      },
      marketState: gameState.marketState ? {
        totalMarketSize: gameState.marketState.totalMarketSize || 500000000,
        marketGrowthRate: gameState.marketState.marketGrowthRate || 0.08,
        economicPhase: gameState.marketState.economicPhase || 'expansion',
        competitorActions: gameState.marketState.competitorActions || [],
        technologicalDisruptions: gameState.marketState.technologicalDisruptions || [],
        marketEvents: gameState.marketState.marketEvents || [],
        playerActions: gameState.marketState.playerActions || []
      } : {
        totalMarketSize: 500000000,
        marketGrowthRate: 0.08,
        economicPhase: 'expansion',
        competitorActions: [],
        technologicalDisruptions: [],
        marketEvents: [],
        playerActions: []
      },
      eventState: gameState.eventState,
      productionBatches: gameState.productionBatches || [],
      productionLines: gameState.productionLines || [],
      competitorProducts: gameState.competitorProducts || [],
      dailyStats: gameState.dailyStats ? {
        revenue: isNaN(gameState.dailyStats.revenue) ? 0 : gameState.dailyStats.revenue,
        costs: isNaN(gameState.dailyStats.costs) ? 0 : gameState.dailyStats.costs,
        profit: isNaN(gameState.dailyStats.profit) ? 0 : gameState.dailyStats.profit,
        salesBreakdowns: gameState.dailyStats.salesBreakdowns || []
      } : {
        revenue: 0,
        costs: 0,
        profit: 0,
        salesBreakdowns: []
      },
      inventoryState: gameState.inventoryState,
      spatialState: gameState.spatialState,
      supplyDemandHistory: gameState.supplyDemandHistory,
      financialHistory: gameState.financialHistory || { monthlySnapshots: [] },
      currentMonthAccumulator: gameState.currentMonthAccumulator || { revenue: 0, costs: 0 },
      settings: gameState.settings,
      statistics: gameState.statistics ? {
        totalRevenue: isNaN(gameState.statistics.totalRevenue) ? 0 : gameState.statistics.totalRevenue,
        totalProfit: isNaN(gameState.statistics.totalProfit) ? 0 : gameState.statistics.totalProfit,
        productsLaunched: gameState.statistics.productsLaunched || 0,
        marketSharePeak: isNaN(gameState.statistics.marketSharePeak) ? 0 : gameState.statistics.marketSharePeak,
        bankruptcies: gameState.statistics.bankruptcies || 0,
        playTime: gameState.statistics.playTime || 0
      } : {
        totalRevenue: 0,
        totalProfit: 0,
        productsLaunched: 0,
        marketSharePeak: 0,
        bankruptcies: 0,
        playTime: 0
      }
    };

    return gameSaveSchema.parse(save);
  }

  static async saveGame(save: GameSave, slotId: number): Promise<void> {
    try {
      const saveKey = `${this.SAVE_PREFIX}${slotId}`;
      const saveData = JSON.stringify(save);
      localStorage.setItem(saveKey, saveData);
    } catch (error) {
      console.error('Failed to save game:', error);
      throw new Error('Failed to save game');
    }
  }

  static async loadGame(slotId: number): Promise<GameSave> {
    try {
      const saveKey = `${this.SAVE_PREFIX}${slotId}`;
      const saveData = localStorage.getItem(saveKey);
      
      if (!saveData) {
        throw new Error('No save data found');
      }

      const parsedData = JSON.parse(saveData);
      
      // Convert saveDate string back to Date object
      parsedData.saveDate = new Date(parsedData.saveDate);
      
      // Convert date fields in inventory state if it exists
      if (parsedData.inventoryState) {
        // Convert component inventory dates
        if (parsedData.inventoryState.components) {
          parsedData.inventoryState.components = parsedData.inventoryState.components.map((comp: any) => ({
            ...comp,
            lastOrderDate: comp.lastOrderDate ? new Date(comp.lastOrderDate) : undefined
          }));
        }
        
        // Convert pending order dates
        if (parsedData.inventoryState.pendingOrders) {
          parsedData.inventoryState.pendingOrders = parsedData.inventoryState.pendingOrders.map((order: any) => ({
            ...order,
            orderDate: new Date(order.orderDate),
            expectedDeliveryDate: new Date(order.expectedDeliveryDate),
            deliveryDate: order.deliveryDate ? new Date(order.deliveryDate) : undefined
          }));
        }
      }
      
      // Version compatibility check
      if (parsedData.version !== this.SAVE_VERSION) {
        return this.migrateSave(parsedData);
      }

      // Validate loaded data and transform it
      const validatedSave = gameSaveSchema.parse(parsedData);
      return this.transformLoadedSave(validatedSave);
    } catch (error) {
      console.error('Failed to load game:', error);
      throw new Error('Failed to load game');
    }
  }

  static async listSaves(): Promise<{ id: number; save: GameSave }[]> {
    const saves: { id: number; save: GameSave }[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.SAVE_PREFIX)) {
        const slotId = parseInt(key.replace(this.SAVE_PREFIX, ''));
        try {
          const save = await this.loadGame(slotId);
          saves.push({ id: slotId, save });
        } catch (error) {
          console.warn(`Failed to load save ${slotId}:`, error);
        }
      }
    }

    return saves.sort((a, b) => b.save.saveDate.getTime() - a.save.saveDate.getTime());
  }

  static async deleteSave(slotId: number): Promise<void> {
    const saveKey = `${this.SAVE_PREFIX}${slotId}`;
    localStorage.removeItem(saveKey);
  }

  private static migrateSave(oldSave: Record<string, unknown>): GameSave {
    // Handle migration from 1.0.0 to 1.1.0
    if (oldSave.version === '1.0.0') {
      console.log('Migrating save from 1.0.0 to 1.1.0');
      
      // Add the new fields with default values
      oldSave.marketState = oldSave.marketState || {
        totalMarketSize: 500000000,
        marketGrowthRate: 0.08,
        economicPhase: 'expansion',
        competitorActions: [],
        technologicalDisruptions: [],
        marketEvents: [],
        playerActions: []
      };
      
      oldSave.eventState = oldSave.eventState || {
        eventHistory: [],
        pendingReactiveEvents: [],
        systemicNotifications: [],
        marketConditions: {}
      };
      
      oldSave.productionBatches = oldSave.productionBatches || [];
      
      oldSave.dailyStats = oldSave.dailyStats || {
        revenue: 0,
        costs: 0,
        profit: 0,
        salesBreakdowns: []
      };
      
      // Update version
      oldSave.version = '1.1.0';
    }
    
    // Add competitor products field if it doesn't exist
    if (!oldSave.competitorProducts) {
      oldSave.competitorProducts = [];
    }

    // Migrate products with production + isReleased to released status
    if (oldSave.company && typeof oldSave.company === 'object') {
      const company = oldSave.company as Record<string, unknown>;
      if (Array.isArray(company.products)) {
        company.products = company.products.map((product: Record<string, unknown>) => {
          if (product.status === 'production' && product.metadata &&
              typeof product.metadata === 'object' &&
              (product.metadata as Record<string, unknown>).isReleased === true) {
            return { ...product, status: 'released' };
          }
          return product;
        });
      }
    }

    try {
      return gameSaveSchema.parse(oldSave);
    } catch (error) {
      console.error('Save migration failed:', error);
      throw new Error('Incompatible save version');
    }
  }

  static transformLoadedSave(save: GameSave): GameSave {
    const transformedProducts = save.company.products.map(product => ({
      ...product,
      metadata: {
        ...product.metadata,
        releaseDate: (product.status === 'production' || product.status === 'released') ? product.metadata.releaseDate : undefined
      }
    }));

    // Transform old research format to new format if needed
    let transformedResearch = save.company.research;
    if (transformedResearch && 'categories' in transformedResearch) {
      // This is the old format, convert to new format
      transformedResearch = {
        unlockedNodes: [],
        activeResearch: [],
        researchPoints: save.company.metrics.researchPoints,
        monthlyInvestment: 0,
        rpGenerationRate: 1
      };
    }

    return {
      ...save,
      company: {
        ...save.company,
        products: transformedProducts,
        research: transformedResearch,
        factories: save.company.factories || [],
        marketingCampaigns: save.company.marketingCampaigns || []
      }
    };
  }
}